import React, { useState, useEffect } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Col
} from 'reactstrap';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  usersQueuesAssignedListGet,
  usersQueuesNotAssignedListGet,
  userProcessAssign
} from '../../../store/slices/tenantSlices/configurationSlice';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { toast } from 'react-toastify';
import FormInput from '../../../components/form/FormInput';

const AssignQueueUsersModal = ({ isOpen, onCloseClick, user_id = null }) => {
  const dispatch = useDispatch();
  const [availableQueues, setAvailableQueues] = useState([]);
  const [assignedQueues, setAssignedQueues] = useState([]);
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssigned, setSearchAssigned] = useState('');
  const [filteredAvailableQueues, setFilteredAvailableQueues] = useState([]);
  const [filteredAssignedQueues, setFilteredAssignedQueues] = useState([]);

  useEffect(() => {
    if (user_id && isOpen) {
      fetchUserQueuesAssignedList();
      fetchUserQueuesNotAssignedList();
    }
  }, [user_id, isOpen]);

  const fetchUserQueuesNotAssignedList = async () => {
    try {
      const response = await dispatch(usersQueuesNotAssignedListGet({ user_id })).unwrap();
      if (response) {
        setAvailableQueues(response || []);
        setFilteredAvailableQueues(response || []);
      }
    } catch (error) {
      console.log('Error fetching available queues:', error);
    }
  };

  const fetchUserQueuesAssignedList = async () => {
    try {
      const response = await dispatch(usersQueuesAssignedListGet({ user_id })).unwrap();
      if (response) {
        setAssignedQueues(response || []);
        setFilteredAssignedQueues(response || []);
      }
    } catch (error) {
      console.log('Error fetching assigned queues:', error);
    }
  };

  const onDragEnd = (result) => {
    const { source, destination } = result;

    if (!destination) return;

    const updatedAvailableQueues = [...availableQueues];
    const updatedAssignedQueues = [...assignedQueues];

    if (source.droppableId === 'available' && destination.droppableId === 'assigned') {
      const movedItem = filteredAvailableQueues[source.index];
      updatedAvailableQueues.splice(updatedAvailableQueues.indexOf(movedItem), 1);
      updatedAssignedQueues.splice(destination.index, 0, movedItem);
    }

    if (source.droppableId === 'assigned' && destination.droppableId === 'available') {
      const movedItem = filteredAssignedQueues[source.index];
      updatedAssignedQueues.splice(updatedAssignedQueues.indexOf(movedItem), 1);
      updatedAvailableQueues.splice(destination.index, 0, movedItem);
    }

    setAvailableQueues(updatedAvailableQueues);
    setAssignedQueues(updatedAssignedQueues);
    setFilteredAvailableQueues(
      updatedAvailableQueues.filter((item) =>
        item.name.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
    setFilteredAssignedQueues(
      updatedAssignedQueues.filter((item) =>
        item.name.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  };

  const onSubmit = async () => {
    try {
      const queue_ids = assignedQueues.map((x) => x.id);
      const payload = {
        queue_id: queue_ids.join(','),
        user_id: user_id
      };
      const response = await dispatch(userProcessAssign(payload)).unwrap();
      toast.success(response.message);
      onCloseClick();
      setAvailableQueues([]);
      setAssignedQueues([]);
    } catch (error) {
      console.log('Error during queue assignment:', error);
    }
  };

  useEffect(() => {
    setFilteredAvailableQueues(
      availableQueues.filter((item) =>
        item.name.toLowerCase().includes(searchAvailable.toLowerCase())
      )
    );
  }, [searchAvailable, availableQueues]);

  useEffect(() => {
    setFilteredAssignedQueues(
      assignedQueues.filter((item) =>
        item.name.toLowerCase().includes(searchAssigned.toLowerCase())
      )
    );
  }, [searchAssigned, assignedQueues]);

  useEffect(() => {
    if (!isOpen) {
      setAvailableQueues([]);
      setAssignedQueues([]);
      setFilteredAvailableQueues([]);
      setFilteredAssignedQueues([]);
      setSearchAvailable('');
      setSearchAssigned('');
    }
  }, [isOpen]);

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">Assign Queues</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <Row className="py-6">
          <DragDropContext onDragEnd={onDragEnd}>
            <Row className="py-6">
              <Col xs="6" md="6" sm="6" lg="6" className="mr-10">
                <Card>
                  <CardHeader className="bg-info py-2 text-dark text-center text-white">
                    <div>Available Queues</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search available queues"
                      value={searchAvailable}
                      onChange={(e) => setSearchAvailable(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="available">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAvailableQueues.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAvailableQueues.length > 0 ? (
                            filteredAvailableQueues.map((item, index) => (
                              <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.name || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No available queues</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>

              <Col xs="6" md="6" sm="6" lg="6">
                <Card>
                  <CardHeader className="bg-primary py-2 text-dark text-center text-white">
                    <div>Assigned Queues</div>
                  </CardHeader>
                  <CardBody
                    className="bg-dark-950"
                    style={{ maxHeight: 'calc(100vh - 280px)', overflowY: 'auto', height: 'auto' }}>
                    <FormInput
                      placeholder="Search assigned queues"
                      value={searchAssigned}
                      onChange={(e) => setSearchAssigned(e.target.value)}
                      className="mb-3"
                    />
                    <Droppable droppableId="assigned">
                      {(provided) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.droppableProps}
                          className={
                            filteredAssignedQueues.length === 0
                              ? 'p-2 border mb-3 bg-white rounded-2 text-primary'
                              : ''
                          }>
                          {filteredAssignedQueues.length > 0 ? (
                            filteredAssignedQueues.map((item, index) => (
                              <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                                {(provided) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                    className="p-2 border mb-3 bg-white rounded-2 text-primary">
                                    {item.name || '-'}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          ) : (
                            <div>No assigned queues</div>
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </DragDropContext>
        </Row>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            setAvailableQueues([]);
            setAssignedQueues([]);
            setFilteredAvailableQueues([]);
            setFilteredAssignedQueues([]);
            setSearchAvailable('');
            setSearchAssigned('');
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button
          form="queues"
          type="submit"
          className="px-5 bg-primary border-primary"
          onClick={onSubmit}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AssignQueueUsersModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onCloseClick: PropTypes.func.isRequired,
  user_id: PropTypes.any
};

export default AssignQueueUsersModal;
